import { type Holder, HolderFields } from "@cleanbudget/contract/holder";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { IconAlertCircle, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { Form, FormError, SubmitButton, TextareaField, TextField, useZodForm } from "@/components/form/form";
import { HelpButton } from "@/components/help/help-button";
import { moveId, OrderButtons, scrollToForm } from "@/components/order-buttons";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

const holdersQuery = () => orpc.holder.list.queryOptions();

/**
 * Producten → Houders (#160): the kinds of holder a refill goes on, by the name the size label
 * uses, in the order the label names them, with the foot size and measuring tip the fit guide and
 * the wisserpagina show (#255). Which variant fits which holder is set in the product editor;
 * deleting a holder removes those fits too, and its categories become ordinary category pages.
 */
export const Route = createFileRoute("/_admin/product/holder/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(holdersQuery()),
	component: HoldersPage,
});

/** Every write answers the whole list: it replaces the cached one, and product editors are stale. */
function useShowHolders() {
	const queryClient = useQueryClient();
	return (list: Holder[]) => {
		queryClient.setQueryData(holdersQuery().queryKey, list);
		void queryClient.invalidateQueries({ queryKey: orpc.product.get.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.category.list.key() });
	};
}

function HoldersPage() {
	const { t } = useTranslation("holder");
	const { t: tProduct } = useTranslation("product");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const { data: holders } = useSuspenseQuery(holdersQuery());
	const show = useShowHolders();
	const [editingId, setEditingId] = useState<string>();
	const [problem, setProblem] = useState<string>();
	const [announcement, setAnnouncement] = useState("");
	const editing = holders.find((holder) => holder.id === editingId);

	const reorder = useMutation({
		...orpc.holder.reorder.mutationOptions(),
		scope: { id: "holder-order" },
		onSuccess: show,
		onError: (error) => {
			setProblem(
				apiErrorTag(error) === "HolderOrderOutdated" ? t("error.orderOutdated") : tCommon("form.failed"),
			);
			return queryClient.invalidateQueries({ queryKey: holdersQuery().queryKey });
		},
	});
	const remove = useMutation({ ...orpc.holder.delete.mutationOptions(), onSuccess: show });

	const columns: readonly Column<Holder>[] = [
		{
			id: "position",
			header: t("column.position"),
			secondary: true,
			cell: (holder) => <span className="text-muted-foreground tabular-nums">{holder.position + 1}</span>,
		},
		{
			id: "name",
			header: t("column.name"),
			cell: (holder) => (
				<span className="flex flex-col gap-0.5">
					<span className="font-medium whitespace-normal">{holder.name}</span>
					{holder.singularName ? (
						<span className="text-xs whitespace-normal text-muted-foreground">
							{t("singular", { name: holder.singularName })}
						</span>
					) : null}
					{holder.footSize ? (
						<span className="text-xs whitespace-normal text-muted-foreground">
							{t("footSize", { size: holder.footSize })}
						</span>
					) : null}
					<span className="text-xs text-muted-foreground md:hidden">
						{t("variantCount", { count: holder.variantCount })}
					</span>
				</span>
			),
		},
		{
			id: "variants",
			header: t("column.variants"),
			secondary: true,
			align: "right",
			cell: (holder) => (
				<span className="tabular-nums">{t("variantCount", { count: holder.variantCount })}</span>
			),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("column.actions")}</span>,
			align: "right",
			cell: (holder) => {
				const index = holders.indexOf(holder);
				return (
					<span className="flex justify-end gap-1">
						<OrderButtons
							index={index}
							count={holders.length}
							onMove={(to) => {
								setProblem(undefined);
								reorder.mutate({
									ids: moveId(
										holders.map((entry) => entry.id),
										index,
										to,
									),
								});
								setAnnouncement(t("moved", { name: holder.name, position: to + 1, total: holders.length }));
							}}
							upLabel={t("moveUp", { name: holder.name })}
							downLabel={t("moveDown", { name: holder.name })}
						/>
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label={t("edit.button", { name: holder.name })}
							onClick={() => {
								setEditingId(holder.id);
								scrollToForm("holder-form");
							}}
						>
							<IconPencil aria-hidden />
						</Button>
						<ConfirmDialog
							trigger={
								<Button variant="ghost" size="icon-sm" aria-label={t("delete.button", { name: holder.name })}>
									<IconTrash aria-hidden />
								</Button>
							}
							title={t("delete.title", { name: holder.name })}
							description={[
								t("delete.description", { count: holder.variantCount }),
								holder.categoryCount > 0 ? t("delete.categories", { count: holder.categoryCount }) : "",
							]
								.filter(Boolean)
								.join(" ")}
							confirmLabel={t("delete.confirm")}
							onConfirm={async () => {
								await remove.mutateAsync({ holderId: holder.id });
								if (editingId === holder.id) setEditingId(undefined);
							}}
						/>
					</span>
				);
			},
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/product">{tProduct("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				help="holder:holders"
			/>
			{problem ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{problem}</AlertDescription>
				</Alert>
			) : null}
			<div className="flex flex-col gap-3">
				{holders.length > 1 ? <p className="text-sm text-muted-foreground">{t("orderHint")}</p> : null}
				<DataTable
					label={t("list")}
					columns={columns}
					rows={holders}
					rowKey={(holder) => holder.id}
					empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("empty")}</p>}
				/>
			</div>
			<div id="holder-form" className="scroll-mt-4">
				{editing ? (
					<HolderForm key={editing.id} holder={editing} onDone={() => setEditingId(undefined)} />
				) : (
					<HolderForm />
				)}
			</div>
			<p aria-live="polite" className="sr-only">
				{announcement}
			</p>
		</div>
	);
}

/** A new holder, or (with `holder`) its new fields. Empty clears an optional one. */
function HolderForm({ holder, onDone }: { holder?: Holder; onDone?: () => void }) {
	const { t } = useTranslation("holder");
	const { t: tCommon } = useTranslation();
	const show = useShowHolders();
	const form = useZodForm(HolderFields, {
		defaultValues: {
			name: holder?.name ?? "",
			singularName: holder?.singularName ?? "",
			footSize: holder?.footSize ?? "",
			measuringTip: holder?.measuringTip ?? "",
		},
	});
	const create = useMutation({ ...orpc.holder.create.mutationOptions(), onSuccess: show });
	const update = useMutation({ ...orpc.holder.update.mutationOptions(), onSuccess: show });

	const onSubmit = async (values: HolderFields) => {
		try {
			if (holder) await update.mutateAsync({ holderId: holder.id, ...values });
			else await create.mutateAsync(values);
			form.reset({ name: "", singularName: "", footSize: "", measuringTip: "" });
			onDone?.();
		} catch (error) {
			match(apiErrorTag(error))
				.with("HolderNameTaken", () =>
					form.setError("name", { type: "server", message: t("error.nameTaken") }),
				)
				.with("HolderNotFound", () => form.setError("root", { type: "server", message: t("error.notFound") }))
				.otherwise(() => form.setError("root", { type: "server", message: tCommon("form.failed") }));
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-1.5">
					{holder ? t("edit.title", { name: holder.name }) : t("new.title")}
					<HelpButton topic="product:holder" />
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Form form={form} onSubmit={onSubmit}>
					<TextField
						control={form.control}
						name="name"
						label={t("field.name")}
						help="holder:name"
						description={t("field.nameDescription")}
						placeholder={t("field.namePlaceholder")}
						autoComplete="off"
					/>
					<TextField
						control={form.control}
						name="singularName"
						label={t("field.singularName")}
						description={t("field.singularNameDescription")}
						placeholder={t("field.singularNamePlaceholder")}
						autoComplete="off"
					/>
					<TextField
						control={form.control}
						name="footSize"
						label={t("field.footSize")}
						help="holder:fitGuide"
						description={t("field.footSizeDescription")}
						placeholder={t("field.footSizePlaceholder")}
						autoComplete="off"
						className="max-w-48"
					/>
					<TextareaField
						control={form.control}
						name="measuringTip"
						label={t("field.measuringTip")}
						help="holder:fitGuide"
						description={t("field.measuringTipDescription")}
						placeholder={t("field.measuringTipPlaceholder")}
						rows={2}
					/>
					<FormError control={form.control} />
					<div className="flex flex-wrap gap-2">
						<SubmitButton control={form.control}>{holder ? t("edit.submit") : t("new.submit")}</SubmitButton>
						{holder ? (
							<Button type="button" variant="outline" onClick={onDone}>
								{t("edit.cancel")}
							</Button>
						) : null}
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}
